import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FinanceRate } from '@kanda-libs/ks-frontend-services';
import {
  ValidationItems,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import { formatToCurrency } from 'utils';
import { useCurrentJob } from 'hooks';

import {
  CREDIT_AMOUNT_FIELD_NAME,
  DEPOSIT_AMOUNT_FIELD_NAME,
  BASE_VALIDATION,
} from './constants';
import { FINANCE_OPTIONS } from '../CreditLines/constants';

export interface CalculatorHook {
  isLoading: boolean;
  maxCredit: string;
  minDeposit: string;
  quotePrice: string;
  depositAmount: string;
  creditAmountValidation: ValidationItems;
  onBlur: () => void;
}

function useCalculator(): CalculatorHook {
  const { job } = useCurrentJob();
  const { setValue, trigger } = useFormContext();

  const creditAmount = useWatch({ name: CREDIT_AMOUNT_FIELD_NAME });

  const maxCredit = useMemo(() => {
    if (!job) return 0;
    const options = job?.finance_options || FINANCE_OPTIONS;
    return (
      options
        .map((option: FinanceRate) => option?.credit_line || 0)
        ?.sort((line1: number, line2: number) => line1 - line2)?.[0] || 0
    );
  }, [job]);

  const minDeposit = useMemo(() => {
    if (!job) return 0;
    if (job?.deposit_type === 'no_deposit') return 0;
    return job?.deposit_value?.amount || 0;
  }, [job]);

  const depositAmount = useMemo(() => {
    if (!job) return 0;
    const total: number = job?.total?.amount_vat_inclusive?.amount || 0;
    const minAllowedDeposit: number =
      job?.deposit_type === 'no_deposit' ? 0 : job?.deposit_value?.amount || 0;
    const chosenDeposit = total - creditAmount;
    return Math.max(minAllowedDeposit, chosenDeposit);
  }, [creditAmount, job]);

  const quotePrice = useMemo(() => {
    if (!job) return 0;
    return job?.total?.amount_vat_inclusive?.amount || 0;
  }, [job]);

  const creditAmountValidation = useMemo(() => {
    if (!job) return BASE_VALIDATION;
    const total: number = job?.total?.amount_vat_inclusive?.amount || 0;
    const deposit: number =
      job?.deposit_type === 'no_deposit' ? 0 : job?.deposit_value?.amount || 0;
    const maxCanCredit = Math.min(maxCredit, total - deposit);
    return {
      ...BASE_VALIDATION,
      max: {
        value: total - deposit,
        message: `A maximum of ${formatToCurrency(
          maxCanCredit,
        )} can be used for credit`,
      },
    };
  }, [job, maxCredit]);

  const onBlur = useCallback(
    () => trigger(CREDIT_AMOUNT_FIELD_NAME),
    [trigger],
  );

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (!job || initRef.current) return;
    const total: number = job?.total?.amount_vat_inclusive?.amount || 0;
    const deposit: number =
      job?.deposit_type === 'no_deposit' ? 0 : job?.deposit_value?.amount || 0;
    setValue(CREDIT_AMOUNT_FIELD_NAME, total - deposit);
    setValue(DEPOSIT_AMOUNT_FIELD_NAME, deposit);
    initRef.current = true;
  }, [setValue, job]);

  return {
    isLoading: !job,
    maxCredit: formatToCurrency(maxCredit),
    minDeposit: formatToCurrency(minDeposit),
    quotePrice: formatToCurrency(quotePrice),
    depositAmount: formatToCurrency(depositAmount),
    creditAmountValidation,
    onBlur,
  };
}

export default useCalculator;
